import React, { createContext, useContext, useEffect, useState } from "react";
import { authApi } from "@/lib/api";
import {
  decodeJwtPayload,
  getUserFromOtpResponse,
  isAdminRole,
  parseOtpMessage,
} from "@/lib/authToken";

const ListingAuthContext = createContext();
const STORAGE_KEY = "omnivaas.listingAuth";
const OTP_ID_KEY = "omnivaas.listingOtpId";
const PENDING_MOBILE_KEY = "omnivaas.listingPendingMobile";
const LEGACY_PENDING_EMAIL_KEY = "omnivaas.listingPendingEmail";

function isExpired(exp) {
  return Number(exp || 0) * 1000 <= Date.now();
}

function clearStoredAuth() {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(OTP_ID_KEY);
  localStorage.removeItem(PENDING_MOBILE_KEY);
  localStorage.removeItem(LEGACY_PENDING_EMAIL_KEY);
}

function getValidatedListingUser(user) {
  if (!user?.token) {
    return null;
  }

  const decodedToken = decodeJwtPayload(user.token);
  const exp = user.exp || decodedToken?.exp;
  const role = user.role || decodedToken?.role || "";

  if (!exp || isExpired(exp) || isAdminRole(role)) {
    return null;
  }

  return {
    ...decodedToken,
    ...user,
    exp: Number(exp),
    role,
  };
}

function loadStoredAuth() {
  if (typeof window === "undefined") {
    return { currentUser: null, isAuthenticated: false };
  }

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return { currentUser: null, isAuthenticated: false };
  }

  try {
    const parsed = JSON.parse(raw);
    const currentUser = getValidatedListingUser(parsed.currentUser || null);

    if (parsed.isAuthenticated && !currentUser) {
      clearStoredAuth();
      return { currentUser: null, isAuthenticated: false };
    }

    return {
      currentUser,
      isAuthenticated: !!parsed.isAuthenticated && !!currentUser,
    };
  } catch {
    clearStoredAuth();
    return { currentUser: null, isAuthenticated: false };
  }
}

export function useListingAuth() {
  return useContext(ListingAuthContext);
}

export function ListingAuthProvider({ children }) {
  const storedAuth = loadStoredAuth();
  const [currentUser, setCurrentUser] = useState(storedAuth.currentUser);
  const [isAuthenticated, setIsAuthenticated] = useState(
    storedAuth.isAuthenticated,
  );
  const [otpId, setOtpId] = useState(
    typeof window === "undefined"
      ? null
      : localStorage.getItem(OTP_ID_KEY),
  );
  const [pendingMobile, setPendingMobile] = useState(
    typeof window === "undefined"
      ? ""
      : localStorage.getItem(PENDING_MOBILE_KEY) || "",
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (currentUser?.token && isAuthenticated) {
      const validatedUser = getValidatedListingUser(currentUser);

      if (!validatedUser) {
        clearStoredAuth();
        setCurrentUser(null);
        setIsAuthenticated(false);
        return;
      }

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ currentUser: validatedUser, isAuthenticated: true }),
      );
      return;
    }

    if (!pendingMobile) {
      clearStoredAuth();
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [currentUser, isAuthenticated, otpId, pendingMobile]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const validatedUser = getValidatedListingUser(currentUser);

    if (!validatedUser) {
      logout();
      return undefined;
    }

    const expiresIn = Number(validatedUser.exp) * 1000 - Date.now();

    if (expiresIn <= 0) {
      logout();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      logout();
    }, expiresIn);

    return () => window.clearTimeout(timeoutId);
  }, [currentUser, isAuthenticated]);

  const requestOTP = async (mobile) => {
    try {
      const normalizedMobile = String(mobile || "").trim();
      const data = await authApi.requestOtp({
        mobile: normalizedMobile,
        scope: "listing",
      });

      setPendingMobile(normalizedMobile);
      if (typeof window !== "undefined") {
        localStorage.setItem(PENDING_MOBILE_KEY, normalizedMobile);
        localStorage.removeItem(LEGACY_PENDING_EMAIL_KEY);
      }
      setOtpId(data?.otpId || null);
      if (typeof window !== "undefined") {
        if (data?.otpId) {
          localStorage.setItem(OTP_ID_KEY, data.otpId);
        } else {
          localStorage.removeItem(OTP_ID_KEY);
        }
      }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const verifyOTP = async (code) => {
    let data;

    try {
      data = await authApi.verifyOtp({
        mobile: pendingMobile,
        otp: code,
      });
    } catch {
      throw new Error("Invalid or expired verification code. Please try again.");
    }

    const otpMessage = parseOtpMessage(data?.message);

    if (!otpMessage.isVerificationPassed) {
      throw new Error("Invalid or expired verification code. Please try again.");
    }

    const user = getUserFromOtpResponse(data, pendingMobile) || {
      mobile: pendingMobile,
      role: "listing-user",
    };

    if (isAdminRole(user?.role)) {
      clearStoredAuth();
      setCurrentUser(null);
      setIsAuthenticated(false);
      setOtpId(null);
      setPendingMobile("");

      return {
        ...data,
        decodedUser: user,
      };
    }

    setCurrentUser(user);
    setIsAuthenticated(true);
    setOtpId(null);
    setPendingMobile("");
    if (typeof window !== "undefined") {
      localStorage.removeItem(OTP_ID_KEY);
      localStorage.removeItem(PENDING_MOBILE_KEY);
      localStorage.removeItem(LEGACY_PENDING_EMAIL_KEY);
    }

    return {
      ...data,
      decodedUser: user,
    };
  };

  const logout = () => {
    clearStoredAuth();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setOtpId(null);
    setPendingMobile("");
  };

  return (
    <ListingAuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        requestOTP,
        verifyOTP,
        logout,
        otpId,
        pendingMobile,
        pendingEmail: pendingMobile,
      }}
    >
      {children}
    </ListingAuthContext.Provider>
  );
}
